import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, X, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ProfileTemplatePicker from "@/components/wizard/ProfileTemplatePicker";
import FieldHelp from "@/components/FieldHelp";
import { createProfile, updateProfile, deleteProfile } from "@/lib/profiles";

function num(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const s = String(v).replace(/\s/g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function Field({ label, help, suffix, value, onChange, type = "number", step = "any", min, testId, placeholder }) {
  return (
    <div>
      <Label className="flex items-center text-[11px] uppercase tracking-widest text-muted-foreground">
        <span>{label}</span>
        {help ? <FieldHelp {...help} /> : null}
      </Label>
      <div className="mt-1 relative">
        <Input
          type={type}
          step={step}
          min={min}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 pr-14"
          data-testid={testId}
          placeholder={placeholder}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * ProfileForm — a card-sized editor for one contributor profile.
 * Props:
 *   index           : 0..2 — visual "Profil #N" label
 *   templates       : predefined templates
 *   value           : current profile (server-persisted or draft)
 *   projectId       : parent project (for create)
 *   onSaved(fresh)  : after create/update
 *   onRemoved(id)   : after delete
 */
export default function ProfileForm({
  index,
  templates,
  value,
  projectId,
  onSaved,
  onRemoved,
}) {
  // Working copy
  const [draft, setDraft] = useState(() => ({
    name: value?.name || `Profil ${index + 1}`,
    template_key: value?.template_key || "custom",
    monthly_salary: value?.monthly_salary ?? 0,
    work_days: value?.work_days ?? 30,
    ir_rate: value?.ir_rate ?? 0,
    social_rate: value?.social_rate ?? 0,
    consumption: value?.consumption ?? 0,
    vat_rate: value?.vat_rate ?? 0,
    other_taxes: value?.other_taxes ?? 0,
    active_years: value?.active_years ?? 40,
  }));
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const persistedId = value?.id || null;

  // Keep draft in sync when `value` changes (after save)
  useEffect(() => {
    if (value) {
      setDraft({
        name: value.name,
        template_key: value.template_key || "custom",
        monthly_salary: value.monthly_salary,
        work_days: value.work_days,
        ir_rate: value.ir_rate,
        social_rate: value.social_rate,
        consumption: value.consumption,
        vat_rate: value.vat_rate,
        other_taxes: value.other_taxes,
        active_years: value.active_years,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id]);

  const set = (key, v) => setDraft((d) => ({ ...d, [key]: v }));

  const applyTemplate = (t) => {
    if (t.key === "custom") {
      set("template_key", "custom");
      return;
    }
    setDraft((d) => ({
      ...d,
      template_key: t.key,
      name: d.name && !d.name.startsWith("Profil ") ? d.name : t.name,
      ...t.defaults,
    }));
  };

  // Live daily wage preview (client-side, pedagogical)
  const dailyWage = useMemo(() => {
    const s = num(draft.monthly_salary);
    const w = num(draft.work_days) || 1;
    return s / w;
  }, [draft.monthly_salary, draft.work_days]);

  const canSave = useMemo(() => {
    return (
      String(draft.name || "").trim().length >= 2 &&
      num(draft.monthly_salary) > 0 &&
      num(draft.work_days) > 0 &&
      num(draft.active_years) > 0
    );
  }, [draft]);

  const handleSave = async () => {
    if (!canSave) {
      toast.error("Nom ≥ 2 chars, salaire > 0, jours de travail > 0.");
      return;
    }
    const payload = {
      name: String(draft.name).trim(),
      template_key: draft.template_key || "custom",
      monthly_salary: num(draft.monthly_salary),
      work_days: num(draft.work_days),
      ir_rate: num(draft.ir_rate),
      social_rate: num(draft.social_rate),
      consumption: num(draft.consumption),
      vat_rate: num(draft.vat_rate),
      other_taxes: num(draft.other_taxes),
      active_years: num(draft.active_years),
    };
    setSaving(true);
    try {
      const fresh = persistedId
        ? await updateProfile(persistedId, payload)
        : await createProfile(projectId, payload);
      toast.success(persistedId ? "Profil mis à jour." : "Profil créé.");
      onSaved?.(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer le profil.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!persistedId) {
      onRemoved?.(null);
      return;
    }
    setRemoving(true);
    try {
      await deleteProfile(persistedId);
      toast.success("Profil supprimé.");
      onRemoved?.(persistedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de supprimer le profil.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Card data-testid={`profile-form-${index}`} className="border-brand-gold/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {index + 1}
            </span>
            Profil contribuable #{index + 1}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            disabled={removing || saving}
            aria-label="Retirer ce profil"
            data-testid={`profile-remove-${index}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <ProfileTemplatePicker
          templates={templates}
          selectedKey={draft.template_key}
          onSelect={applyTemplate}
          disabled={saving}
        />

        <Separator />

        <div>
          <Label htmlFor={`profile-name-${index}`} className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Nom du profil
          </Label>
          <Input
            id={`profile-name-${index}`}
            className="mt-1"
            value={draft.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ex : Fonctionnaire moyen, Ouvrier SMIG…"
            data-testid={`profile-name-input-${index}`}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field
            label="Salaire mensuel net"
            help={{
              id: "monthly-salary",
              title: "Salaire mensuel net",
              description: "Rémunération nette versée chaque mois. Sert de base de calcul vitaliste : le salaire journalier de référence est ce salaire ÷ 30 (la vie ne s'arrête pas le week-end).",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="UM"
            value={draft.monthly_salary}
            onChange={(v) => set("monthly_salary", v)}
            min={0}
            testId={`profile-monthly-salary-${index}`}
          />
          <Field
            label="Jours travaillés / mois"
            value={draft.work_days}
            onChange={(v) => set("work_days", v)}
            min={1}
            testId={`profile-work-days-${index}`}
          />
          <Field
            label="Carrière"
            help={{
              id: "career-years",
              title: "Carrière professionnelle",
              description: "Durée estimée de votre vie active. Permet de cumuler les jours de vie sacrifiés sur l'ensemble d'une carrière (souvent 35 à 42 ans).",
              chapter: 3,
              section: "section-3-4",
            }}
            suffix="ans"
            value={draft.active_years}
            onChange={(v) => set("active_years", v)}
            min={1}
            testId={`profile-active-years-${index}`}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field
            label="Taux IR"
            help={{
              id: "ir-rate",
              title: "Taux d'IR effectif",
              description: "Pourcentage moyen prélevé sur le revenu annuel : IR / revenu annuel × 100. Tient compte des abattements et tranches.",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="%"
            value={draft.ir_rate}
            onChange={(v) => set("ir_rate", v)}
            min={0}
            testId={`profile-ir-rate-${index}`}
          />
          <Field
            label="Cotisations sociales"
            help={{
              id: "social-rate",
              title: "Cotisations sociales",
              description: "Pourcentage prélevé sur le salaire brut au titre des régimes de santé, retraite, chômage. Plafonné selon le pays.",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="%"
            value={draft.social_rate}
            onChange={(v) => set("social_rate", v)}
            min={0}
            testId={`profile-social-rate-${index}`}
          />
          <Field
            label="TVA applicable"
            help={{
              id: "vat-rate",
              title: "Taux moyen de TVA",
              description: "Taux pondéré appliqué à votre consommation imposable annuelle. Détermine la part vitaliste prélevée à la consommation.",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="%"
            value={draft.vat_rate}
            onChange={(v) => set("vat_rate", v)}
            min={0}
            testId={`profile-vat-rate-${index}`}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Consommation mensuelle imposable"
            help={{
              id: "consumption",
              title: "Consommation imposable",
              description: "Montant mensuel des dépenses soumises à la TVA. Sert à calculer la TVA annuelle = consommation × taux × 12.",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="UM"
            value={draft.consumption}
            onChange={(v) => set("consumption", v)}
            min={0}
            testId={`profile-consumption-${index}`}
          />
          <Field
            label="Autres taxes annuelles"
            help={{
              id: "other-taxes",
              title: "Autres prélèvements",
              description: "Taxes spécifiques cumulées : carburant, alcool, tabac, accises diverses. Permet d'affiner le profil contribuable.",
              chapter: 3,
              section: "section-3-3",
            }}
            suffix="UM"
            value={draft.other_taxes}
            onChange={(v) => set("other_taxes", v)}
            min={0}
            testId={`profile-other-taxes-${index}`}
          />
        </div>

        <div className="rounded-md border border-brand-gold/30 bg-accent/30 p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calculator className="h-3.5 w-3.5" />
            Aperçu du salaire journalier
          </div>
          <div className="mt-1 font-mono text-lg font-bold tabular-nums">
            {dailyWage.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}{" "}
            <span className="text-xs font-medium text-muted-foreground">
              UM / jour
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canSave || saving}
            data-testid={`profile-save-${index}`}
          >
            <Save className="mr-1 h-3.5 w-3.5" />
            {saving
              ? "Enregistrement…"
              : persistedId
                ? "Mettre à jour"
                : "Enregistrer ce profil"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
